// @file Surface map store
import device from '@@/bits/device'
import window from '@@/bits/global'
import type { JsonAPIResource, JsonAPIResponse, WallTheme } from '@padlet/arvo'
import { fetchMapThemes as arvoFetchMapThemes } from '@padlet/arvo'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useWindowSizeStore } from 'pinia/window_size'
import type { Cid, Id } from 'types'
import { computed, ref } from 'vue'

export const useSurfaceMapStore = defineStore('surfaceMap', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const { mapThemeId: wallThemeId, isEmbedded } = storeToRefs(useSurfaceStore())
  const { isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())

  const mapThemes = ref<Record<number, WallTheme>>()
  const isFetchingMapThemes = ref(false)

  const currentThemeId = computed<number>(() => surfaceVuexStore?.getters.themeId)
  const currentMap = computed(() => (mapThemes.value !== undefined ? mapThemes.value[currentThemeId.value] : undefined))
  const isMapThemeChanged = computed<boolean>(() => currentThemeId.value !== wallThemeId.value)
  const postListPosition = computed<'side' | 'bottom'>(() =>
    isSmallerThanTabletLandscape.value || device.app || isEmbedded.value ? 'bottom' : 'side',
  )

  function startPickingLocation(payload?: { postCid?: Cid; sectionId?: Id }): void {
    void surfaceVuexStore?.dispatch('map/startPickingLocation', payload)
  }

  function highlightPostMarker(payload: { postCid: Cid | null }): void {
    void surfaceVuexStore?.dispatch('map/highlightPostMarker', payload)
  }

  function openPostPopup(payload: { postCid: Cid }): void {
    void surfaceVuexStore?.dispatch('map/openPostPopup', payload)
  }

  function startDraggingPosts(): void {
    void surfaceVuexStore?.dispatch('map/startDraggingPosts')
  }

  function stopDraggingPosts(): void {
    void surfaceVuexStore?.dispatch('map/stopDraggingPosts')
  }

  async function fetchMapThemes(): Promise<void> {
    isFetchingMapThemes.value = true
    const themesResponse: JsonAPIResponse<WallTheme> = await arvoFetchMapThemes()
    if (themesResponse.data != null) {
      const mapThemeDictionary = {}
      const allThemes = themesResponse.data as Array<JsonAPIResource<WallTheme>>
      allThemes.forEach((mapTheme: JsonAPIResource<WallTheme>) => {
        const details = mapTheme.attributes
        mapThemeDictionary[details.id] = details
      })
      mapThemes.value = mapThemeDictionary
    }
    isFetchingMapThemes.value = false
  }

  return {
    mapThemes,
    currentThemeId,
    isMapThemeChanged,
    currentMap,
    isFetchingMapThemes,
    postListPosition,

    // actions
    fetchMapThemes,
    startPickingLocation,
    highlightPostMarker,
    openPostPopup,
    startDraggingPosts,
    stopDraggingPosts,
  }
})
